import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ForceDeleteDialog } from "@/components/ForceDeleteDialog";
import { MachineForm } from "@/components/MachineForm";
import { ZoomableImage } from "@/components/ZoomableImage";
import { Cog, Plus, ArrowRight, Trash2, Pencil } from "lucide-react";

const STATUS_DOT = { OPERANDO: "bg-emerald-400", MANUTENCAO: "bg-amber-400", PARADA: "bg-rose-400" };
const STATUS_LABEL = { OPERANDO: "Operando", MANUTENCAO: "Manutenção", PARADA: "Parada" };

export default function Machines() {
  const { can } = useAuth();
  const isAdmin = can();
  const navigate = useNavigate();
  const [machines, setMachines] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/machines").then((r) => setMachines(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (e, m) => { e.stopPropagation(); setEditing(m); setFormOpen(true); };

  const [del, setDel] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const openDelete = async (e, m) => {
    e.stopPropagation();
    try {
      const { data } = await api.get(`/machines/${m.id}/has-history`);
      setDel({ id: m.id, name: m.nome, hasHistory: data.has_history });
    } catch (err) { toast.error(apiErr(err)); }
  };
  const confirmDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/machines/${del.id}`, { params: del.hasHistory ? { force: true } : {} });
      toast.success("Máquina excluída definitivamente.");
      setDel(null); load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setDelBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div><h1 className="font-display text-3xl font-bold text-slate-50">Máquinas</h1><p className="text-slate-400 text-sm">Equipamentos industriais e suas peças relacionadas.</p></div>
        {can() && <Button data-testid="btn-new-machine" onClick={openNew} className="bg-sky-700 hover:bg-sky-600"><Plus className="h-4 w-4 mr-1" /> Nova máquina</Button>}
      </div>

      {machines === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-56 bg-slate-800" />)}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {machines.map((m) => (
            <Card key={m.id} data-testid={`machine-card-${m.id}`} onClick={() => navigate(`/maquinas/${m.id}`)} className="card-metal p-0 overflow-hidden cursor-pointer group animate-in-up relative">
              {isAdmin && (
                <div className="absolute top-3 left-3 z-10 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`btn-edit-machine-${m.id}`} onClick={(e) => openEdit(e, m)} title="Editar máquina" className="rounded-md bg-slate-950/70 p-1.5 text-sky-300 hover:text-sky-200 hover:bg-sky-950/60"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`btn-delete-machine-${m.id}`} onClick={(e) => openDelete(e, m)} title="Excluir definitivamente" className="rounded-md bg-slate-950/70 p-1.5 text-rose-400 hover:text-rose-300 hover:bg-rose-950/60"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
              <div className="h-36 bg-slate-950 overflow-hidden relative">
                {m.foto ? <ZoomableImage src={m.foto} alt={m.nome} testid={`machine-photo-${m.id}`} className="h-full w-full object-cover group-hover:scale-105 transition-transform" /> : <div className="h-full flex items-center justify-center"><Cog className="h-10 w-10 text-slate-700" /></div>}
                <span className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full bg-slate-950/80 px-2 py-1 text-xs">
                  <span className={`h-2 w-2 rounded-full status-dot ${STATUS_DOT[m.status] || "bg-slate-400"}`} /> {m.status}
                </span>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl font-bold text-slate-50">{m.nome}</h3>
                  <span className="font-mono text-xs text-sky-400">{m.codigo}</span>
                </div>
                <div className="text-xs text-slate-500">{m.tipo} · {m.fabricante} {m.modelo}</div>
                <div className="font-mono text-[11px] text-sky-400 mt-0.5">{m.equipment_id}{m.ativo === false && <span className="ml-2 text-rose-400">· INATIVA</span>}</div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-slate-950/60 py-2"><div className="text-lg font-bold text-slate-100 font-mono">{m.total_pecas}</div><div className="text-[10px] uppercase text-slate-500">peças</div></div>
                  <div className="rounded-lg bg-rose-950/40 py-2"><div className="text-lg font-bold text-rose-300 font-mono">{m.pecas_criticas}</div><div className="text-[10px] uppercase text-rose-400/70">críticas</div></div>
                  <div className="rounded-lg bg-amber-950/40 py-2"><div className="text-lg font-bold text-amber-300 font-mono">{m.pecas_risco}</div><div className="text-[10px] uppercase text-amber-400/70">risco</div></div>
                </div>
                <div className="mt-3 flex items-center justify-end text-sm text-sky-400 group-hover:text-sky-300">Ver detalhes <ArrowRight className="h-4 w-4 ml-1" /></div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <MachineForm open={formOpen} onOpenChange={setFormOpen} machine={editing} onSaved={load} />
      <ForceDeleteDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} entityLabel="a máquina" name={del?.name} hasHistory={del?.hasHistory} busy={delBusy} onConfirm={confirmDelete} />
    </div>
  );
}
